import { AxiosRequestConfig } from "axios";
import api, { masonCtlKey } from "../api";
import { Task } from "records/Task";
import { ActivityInstance } from "records/ActivityInstance";
import MasonDocument from "lib/mason";

// API path to the forms service
const path = `workflow`;

/**
 * Start a new activity / workflow
 *
 * @param id workflow id
 */
const start = async (id: string) => {
	return api.request({
		url: `${path}/startworkflowinstance`,
		method: "GET",
		params: { id },
	});
};

/**
 * Get a task instance
 *
 * @param id task id
 */
const getTask = (id: string, customOpts?: AxiosRequestConfig["customOpts"]) => {
	return api.request({
		url: `${path}/workflowtasks?id=${id}`,
		method: "GET",
		customOpts,
	});
};

/**
 * Get a list of tasks assigned to the user
 *
 * @param opts optional axios request params
 */
const getTasks = async (
	params?: { offset: number; limit: number; search?: string },
	opts?: AxiosRequestConfig
) => {
	const res = await api.request({
		method: "GET",
		url: `${path}/mytasks`,
		params,
		...opts,
	});

	return {
		hasMore: MasonDocument.hasControl(res.data, masonCtlKey.workflow.page),
		results: res.data.matches,
		totalCount: res.data.totalCount,
		...(MasonDocument.hasControl(res.data, masonCtlKey.workflow.page) &&
			MasonDocument.getControlTemplate(
				res.data,
				masonCtlKey.workflow.page
			)),
	};
};

/**
 * Get a list of unclaimed tasks that the current user could claim
 *
 * @param opts optional axios request params
 */
const getUnclaimedTasks = async (
	params?: { offset: number; limit: number; search?: string },
	opts?: AxiosRequestConfig
) => {
	const res = await api.request({
		method: "GET",
		url: `${path}/unclaimedtasks`,
		params,
		...opts,
	});

	return {
		hasMore: MasonDocument.hasControl(res.data, masonCtlKey.workflow.page),
		results: res.data.matches,
		totalCount: res.data.totalCount,
		...(MasonDocument.hasControl(res.data, masonCtlKey.workflow.page) &&
			MasonDocument.getControlTemplate(
				res.data,
				masonCtlKey.workflow.page
			)),
	};
};

/**
 * Get a list of workflow definitions
 */
const getDefinitions = async (
	params?: { limit: number; offset: number; search?: string },
	opts?: AxiosRequestConfig
) => {
	const res = await api.request({
		url: `${path}/workflowdefinitions`,
		method: "GET",
		params,
		...opts,
	});

	return {
		hasMore: MasonDocument.hasControl(res.data, masonCtlKey.workflow.page),
		results: res.data.matches,
		...(MasonDocument.hasControl(res.data, masonCtlKey.workflow.page) &&
			MasonDocument.getControlTemplate(
				res.data,
				masonCtlKey.workflow.page
			)),
	};
};

/**
 * Complete a task
 *
 * @param id task id
 */
const completeTask = (id: string) => {
	// HACK: Add empty data object - fixes some API issue
	return api.request({
		url: `${path}/completetask/${id}`,
		method: "PATCH",
		data: {},
		customOpts: {
			handleErrors: true,
		},
	});
};

/**
 * Update the priority of a task
 *
 * @param id task id
 * @param priority the new priority
 */
const updateTaskPriority = (id: string, priority: Task["priority"]) => {
	return api.request({
		method: "PATCH",
		url: `${path}/changetaskpriority/${id}`,
		data: { priority },
		customOpts: {
			handleErrors: true,
		},
	});
};

/**
 * Claim a task (by the current user)
 *
 * @param id task id
 */
const claimTask = (id: string) => {
	// HACK: Add an empty json body as payload to stop the backend freaking out. Remove once fixed.
	return api.request({
		url: `${path}/claimtask/${id}`,
		method: "PATCH",
		data: {},
	});
};

/**
 * Claim multiple tasks (by the current user)
 *
 * @param ids task ids
 * @returns object with ids and errors
 */
const claimMultipleTasks = async (taskIds: string[]) => {
	const res = await api.request({
		url: `${path}/claimtasks`,
		method: "POST",
		data: taskIds,
	});

	return res.data;
};

/**
 * Assign a task to a user
 *
 * @param id task id
 * @param username username
 */
const assignTask = (id: string, username: string) => {
	return api.request({
		url: `${path}/assigntask/${id}`,
		method: "PATCH",
		data: { username },
		customOpts: {
			handleErrors: true,
		},
	});
};

/**
 * Unassign a task
 *
 * @param id task id
 */
const unassignTask = (id: string) => {
	return api.request({
		url: `${path}/unassigntask/${id}`,
		method: "GET",
		customOpts: {
			handleErrors: true,
		},
	});
};

/**
 * Delete an activity
 *
 * @param id task id
 * @param customOpts custom options
 */
const deleteActivity = (
	id: string,
	customOpts?: AxiosRequestConfig["customOpts"]
) => {
	return api.request({
		url: `${path}/cancelactivitybytaskid/${id}`,
		method: "DELETE",
		customOpts,
	});
};

/**
 * Get a list of users that a task can be assigned to
 *
 * @param id task id
 */
const getAssignableUsers = (id: string) => {
	return api.request({
		url: `${path}/getreassignmentusers?id=${id}`,
		method: "GET",
		customOpts: {
			handleErrors: true,
		},
	});
};

/**
 * Change the Read/Unread status of a task
 *
 * @param id task id
 * @param taskRead read status update
 */
const updateReadMarker = (id: string, taskRead = true) => {
	return api.request({
		url: `${path}/marktaskasread/${id}`,
		method: "PATCH",
		data: { taskRead },
		customOpts: {
			handleErrors: true,
		},
	});
};

/**
 * Get all workflow definitions
 *
 */
const getAllWorkflowDefinitions = async (
	params?: {
		limit: number;
		offset: number;
		search?: string;
		key?: string;
	},
	opts?: AxiosRequestConfig
) => {
	const res = await api.request({
		url: `${path}/allworkflowdefinitions`,
		method: "GET",
		params,
		...opts,
	});

	return {
		hasMore: MasonDocument.hasControl(res.data, masonCtlKey.workflow.page),
		results: res.data.matches,
		totalCount: res.data.totalCount,
		...(MasonDocument.hasControl(res.data, masonCtlKey.workflow.page) &&
			MasonDocument.getControlTemplate(
				res.data,
				masonCtlKey.workflow.page
			)),
	};
};

/**
 * Delete a workflow definition
 *
 * @param id workflow id
 */
const deleteWorkflowDefinition = (
	id: string,
	customOpts?: AxiosRequestConfig["customOpts"]
) => {
	return api.request({
		url: `${path}/workflowdefinitions/${id}`,
		method: "DELETE",
		customOpts,
	});
};

/**
 * Upload a workflow definition
 *
 */
const uploadWorkflowDefinition = (data: FormData) => {
	return api.request({
		url: `${path}/uploaddefinition`,
		method: "POST",
		data,
	});
};

/**
 * Download a workflow definition
 *
 */
const downloadWorkflowDefinition = (id: string) => {
	return api.request({
		url: `${path}/downloaddefinition/${id}`,
		method: "GET",
	});
};

/**
 * Check if the workflow definition is in use
 *
 */
const checkIsWorkflowDefinitionInUse = (id: string) => {
	return api.request({
		url: `${path}/isworkflowdefinitioninuse/${id}`,
		method: "GET",
	});
};

/**
 * Save the task
 *
 * @param id id of task
 * @param customOpts custom options for axios
 */
const saveTask = (
	id: string,
	customOpts?: AxiosRequestConfig["customOpts"]
) => {
	return api.request({
		url: `${path}/savetask/${id}`,
		method: "PUT",
		customOpts,
	});
};

export type DateOperators = "ALL" | "BETWEEN" | "BEFORE" | "AFTER" | "EXACT";

export type ActivityInstancesPayload = {
	search?: string;
	offset: number;
	limit: number;
	status?: "ACTIVE" | "COMPLETED";
	creator?: string;
	date?: string;
	operator?: DateOperators;
};

/**
 * Get activity instance by id
 * @param id activity instance id
 */
const getActivityInstances = async (params: ActivityInstancesPayload) => {
	const queryParams = params;
	const res = await api.request({
		url: `${path}/workflowhistory/admin`,
		method: "GET",
		params: queryParams,
	});
	return {
		hasMore: MasonDocument.hasControl(res.data, masonCtlKey.workflow.page),
		results: res.data.matches.map(
			(
				instance: ActivityInstance & {
					startTime: string;
					startUserId: string;
				}
			) => {
				instance.status = (instance.status[0].toUpperCase() +
					instance.status.slice(1).toLowerCase()) as
					| "Active"
					| "Completed";
				instance.created = instance.startTime;
				instance.createdBy = instance.startUserId;
				return instance;
			}
		),
		totalCount: res.data.totalCount,
		...(MasonDocument.hasControl(res.data, masonCtlKey.workflow.page) &&
			MasonDocument.getControlTemplate(
				res.data,
				masonCtlKey.workflow.page
			)),
	};
};

/**
 * Get activity instance by id
 * @param id activity instance id
 */
const getActivityInstance = async (activityId: string) => {
	const res = await api.request({
		url: `${path}/workflowhistory/admin/${activityId}`,
		method: "GET",
	});
	const {
		id,
		reason,
		startTime,
		startUserId,
		status,
		taskCount,
		variablesCount,
		workflow,
	} = res.data;
	return {
		id,
		reason,
		status: status[0].toUpperCase() + status.slice(1).toLowerCase(),
		created: startTime,
		createdBy: startUserId,
		taskCount,
		variablesCount,
		workflow,
	};
};

type TaskType = {
	status: "Active" | "Completed";
	dueDate: string;
	priority: "LOW" | "NORMAL" | "HIGH";
	assignee: string;
};

/**
 * Get all tasks of single activity instance
 */
const getActivityInstanceTasks = async (
	id: string,
	params?: {
		limit: number;
		offset: number;
	}
) => {
	const res = await api.request({
		url: `${path}/workflowhistory/admin/tasks?workflowInstanceId=${id}`,
		method: "GET",
		params,
	});
	return {
		hasMore: MasonDocument.hasControl(res.data, masonCtlKey.workflow.page),
		results: res.data.matches.map(
			(
				task: TaskType & {
					name: string;
					startDate: string;
					id: string;
				}
			) => {
				const mappedTask: TaskType & {
					title: string;
					created: string;
					id: string;
				} = {
					title: task.name,
					status: (task.status[0].toUpperCase() +
						task.status.slice(1).toLowerCase()) as
						| "Active"
						| "Completed",
					created: task.startDate,
					dueDate: task.dueDate,
					priority: task.priority,
					assignee: task.assignee,
					id: task.id,
				};
				return mappedTask;
			}
		),
		...(MasonDocument.hasControl(res.data, masonCtlKey.workflow.page) &&
			MasonDocument.getControlTemplate(
				res.data,
				masonCtlKey.workflow.page
			)),
	};
};

/**
 * Get all variables of single activity instance
 */
const getActivityInstanceVariables = async (
	id: string,
	params?: {
		limit: number;
		offset: number;
	}
) => {
	const res = await api.request({
		url: `${path}/workflowhistory/admin/variables/${id}`,
		method: "GET",
		params,
	});
	return {
		hasMore: MasonDocument.hasControl(res.data, masonCtlKey.workflow.page),
		results: res.data.matches,
		...(MasonDocument.hasControl(res.data, masonCtlKey.workflow.page) &&
			MasonDocument.getControlTemplate(
				res.data,
				masonCtlKey.workflow.page
			)),
	};
};

/**
 * Terintate Activity Instance
 * @param id activity instance id
 * @param reason string of terminating
 */
const terminateActivityInstance = async (id: string, reason: string) => {
	const res = await api.request({
		url: `${path}/workflowinstances/${id}`,
		method: "DELETE",
		params: { reason },
	});
	return res;
};

/**
 * Terintate Activity Instance
 * @param ids activity instances ids
 * @param reason string of terminating
 */
const terminateMultipleActivityInstances = async (
	workflowInstanceIds: string[],
	reason: string
) => {
	return api.request({
		url: `${path}/workflowinstances/cancel`,
		method: "POST",
		data: {
			workflowInstanceIds,
			reason,
		},
	});
};

/**
 * Delete activity instance
 * @param id activity instance id
 */
const deleteActivityInstance = async (id: string) => {
	return api.request({
		url: `${path}/workflowhistory/${id}`,
		method: "DELETE",
	});
};

/**
 * Delete activity instance
 * @param ids activity instances ids
 */
const deleteMultipleActivityInstances = async (
	workflowInstanceIds: string[]
) => {
	return api.request({
		url: `${path}/workflowhistory/delete`,
		method: "POST",
		data: workflowInstanceIds,
	});
};

const getTasksAsAdmin =
	(status?: "ACTIVE" | "COMPLETED") =>
	async (params?: { limit: number; offset?: number; search?: string }) => {
		const queryParams = {
			...params,
			status,
		};

		const res = await api.request({
			url: `${path}/workflowhistory/admin/tasks`,
			method: "GET",
			params: queryParams,
		});
		return {
			hasMore: MasonDocument.hasControl(
				res.data,
				masonCtlKey.workflow.page
			),
			results: res.data.matches.map(
				(task: {
					name: string;
					title?: string;
					started?: string;
					startDate: string;
					status: string;
				}) => {
					task.title = task.name;
					task.started = task.startDate;
					task.status =
						task.status[0].toUpperCase() +
						task.status.slice(1).toLowerCase();
					return task;
				}
			),
			totalCount: res.data.totalCount,
			...(MasonDocument.hasControl(res.data, masonCtlKey.workflow.page) &&
				MasonDocument.getControlTemplate(
					res.data,
					masonCtlKey.workflow.page
				)),
		};
	};

const getAllTasksAsAdmin = getTasksAsAdmin();

const getActiveTasksAsAdmin = getTasksAsAdmin("ACTIVE");

const getCompletedTasksAsAdmin = getTasksAsAdmin("COMPLETED");

const getTaskDetail = async (id: string) => {
	const res = await api.request({
		url: `${path}/workflowhistory/admin/tasks/${id}`,
		method: "GET",
	});
	const {
		assignee,
		dueDate,
		endDate,
		formKey,
		name,
		priority,
		startDate,
		status,
		workflowInstanceId,
		workflowDefinitionName,
	} = res.data;
	return {
		assignee,
		dueDate,
		endDate,
		created: startDate,
		priority,
		title: name,
		status: status[0].toUpperCase() + status.slice(1).toLowerCase(),
		formKey,
		activityInstanceId: workflowInstanceId,
		workflow: workflowDefinitionName,
	};
};

const getTaskVariablesAsAdmin = async (
	taskId: string,
	params?: { offset: number; limit: number }
) => {
	const res = await api.request({
		url: `${path}/workflowhistory/admin/tasks/variables/${taskId}`,
		method: "GET",
		params,
	});
	return {
		hasMore: MasonDocument.hasControl(res.data, masonCtlKey.workflow.page),
		results: res.data.matches,
		...(MasonDocument.hasControl(res.data, masonCtlKey.workflow.page) &&
			MasonDocument.getControlTemplate(
				res.data,
				masonCtlKey.workflow.page
			)),
	};
};

export type TasksManagerPayload = {
	roles?: string[];
	users?: string[];
	assignees?: string[];
	search?: string;
	offset?: number;
	limit?: number;
	status?: "ACTIVE" | "COMPLETED";
};

const getTasksAsManager = async (data: TasksManagerPayload) => {
	const res = await api.request({
		url: `${path}/workflowhistory/manager/tasks`,
		method: "POST",
		data,
	});

	return {
		hasMore: MasonDocument.hasControl(res.data, masonCtlKey.workflow.page),
		results: res.data.matches.map(
			(task: {
				name: string;
				title?: string;
				started?: string;
				startDate: string;
				status: string;
			}) => {
				task.title = task.name;
				task.started = task.startDate;
				task.status =
					task.status[0].toUpperCase() +
					task.status.slice(1).toLowerCase();
				return task;
			}
		),
		totalCount: res.data.totalCount,
		...(MasonDocument.hasControl(res.data, masonCtlKey.workflow.page) &&
			MasonDocument.getControlTemplate(
				res.data,
				masonCtlKey.workflow.page
			)),
	};
};

export default {
	start,
	updateTaskPriority,
	getAssignableUsers,
	assignTask,
	claimTask,
	claimMultipleTasks,
	completeTask,
	getTask,
	updateReadMarker,
	getDefinitions,
	getTasks,
	getUnclaimedTasks,
	unassignTask,
	deleteActivity,
	getAllWorkflowDefinitions,
	deleteWorkflowDefinition,
	uploadWorkflowDefinition,
	downloadWorkflowDefinition,
	checkIsWorkflowDefinitionInUse,
	saveTask,
	getAllTasksAsAdmin,
	getActiveTasksAsAdmin,
	getCompletedTasksAsAdmin,
	getTaskDetail,
	getTaskVariablesAsAdmin,
	getActivityInstances,
	getActivityInstance,
	getActivityInstanceTasks,
	getActivityInstanceVariables,
	terminateActivityInstance,
	terminateMultipleActivityInstances,
	deleteActivityInstance,
	deleteMultipleActivityInstances,
	getTasksAsManager,
};
